/**
 * Bulk approve for the Change Review Queue (Corbin, 2026-10-30): which rows it would take,
 * how a row's use of the well reads, and the result in plain words. Pure, so it is tested
 * without rendering. The server (0262 catalog_change_bulk_eligible) re-checks every change;
 * this only decides what the button counts and what the confirm dialog says.
 */
import type { BulkApproveResult, BulkSkipReason, PlatformChange } from './hooks';

/** Well-level types only: operator changes always count 0 haulers but still reach haulers. */
export const BULK_TYPES: ReadonlySet<string> = new Set(['WELL_DEACTIVATED', 'WELL_STATUS_CHANGED']);

export type HaulerFilter = 'any' | 'none' | 'some';

export function matchesHaulerFilter(c: PlatformChange, f: HaulerFilter): boolean {
  if (f === 'none') return c.affectedHaulerCount === 0;
  if (f === 'some') return c.affectedHaulerCount > 0;
  return true;
}

/** Why bulk approve would leave this row out, or null when it would take it. */
export function bulkExclusion(c: PlatformChange): BulkSkipReason | null {
  if (c.reviewStatus !== 'PENDING') return 'not_pending';
  if (!BULK_TYPES.has(c.changeType) || c.wellId === null) return 'type_excluded';
  if (c.affectedHaulerCount > 0) return 'has_haulers';
  if (c.openJobCount > 0) return 'open_jobs';
  if (c.operatorCount > 0) return 'operator_links';
  return null;
}

function plural(n: number, one: string, many: string): string {
  return `${n.toString()} ${n === 1 ? one : many}`;
}

/** "0 haulers · 1 operator · 2 open jobs" — operators and jobs only when above 0. */
export function usageLabel(
  c: Pick<PlatformChange, 'affectedHaulerCount' | 'operatorCount' | 'openJobCount'>,
): string {
  const parts = [plural(c.affectedHaulerCount, 'hauler', 'haulers')];
  if (c.operatorCount > 0) parts.push(plural(c.operatorCount, 'operator', 'operators'));
  if (c.openJobCount > 0) parts.push(plural(c.openJobCount, 'open job', 'open jobs'));
  return parts.join(' · ');
}

export interface BulkPlan {
  readonly ids: string[];
  /** Pending rows in view that bulk approve leaves out, by reason (for the dialog). */
  readonly excluded: ReadonlyMap<BulkSkipReason, number>;
  readonly operatorDeactivations: number;
}

/** What the "Approve all with 0 haulers" button would send, from the rows in view. */
export function planBulk(changes: ReadonlyArray<PlatformChange>): BulkPlan {
  const ids: string[] = [];
  const excluded = new Map<BulkSkipReason, number>();
  let operatorDeactivations = 0;
  for (const c of changes) {
    if (c.reviewStatus !== 'PENDING') continue;
    const why = bulkExclusion(c);
    if (why === null) {
      ids.push(c.id);
    } else if (c.changeType === 'OPERATOR_DEACTIVATED') {
      operatorDeactivations += 1;
    } else {
      excluded.set(why, (excluded.get(why) ?? 0) + 1);
    }
  }
  return { ids, excluded, operatorDeactivations };
}

const REASON_WORDS: Record<BulkSkipReason, (n: number) => string> = {
  not_found: (n) => `${plural(n, 'change', 'changes')} no longer exist`,
  not_pending: (n) => `${plural(n, 'change was', 'changes were')} no longer pending`,
  type_excluded: (n) =>
    `${plural(n, 'operator change needs', 'operator changes need')} a one-by-one review`,
  has_haulers: (n) => `${plural(n, 'change now has', 'changes now have')} haulers`,
  open_jobs: (n) => `${plural(n, 'well has', 'wells have')} open jobs`,
  operator_links: (n) => `${plural(n, 'well is', 'wells are')} used by an operator`,
  error: (n) => `${plural(n, 'change', 'changes')} failed — try again`,
};

/** Reasons in plain words, e.g. "2 wells have open jobs, 1 well is used by an operator". */
export function reasonsInWords(counts: ReadonlyMap<BulkSkipReason, number>): string {
  return [...counts.entries()].map(([r, n]) => REASON_WORDS[r](n)).join(', ');
}

/** "Approved 109. Skipped 8: 5 changes now have haulers, 3 wells have open jobs." */
export function resultInWords(r: BulkApproveResult): string {
  if (r.skipped.length === 0) return `Approved ${r.approved.toString()}.`;
  const counts = new Map<BulkSkipReason, number>();
  for (const s of r.skipped) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1);
  return `Approved ${r.approved.toString()}. Skipped ${r.skipped.length.toString()}: ${reasonsInWords(counts)}.`;
}
